<template>
  <div :class="['expense-card', dark ? 'expense-card-dark' : 'expense-card-light']">
    <div class="expense-card-header">
      <i class="fas fa-receipt text-info" style="font-size: 22px"></i>
      <h3 class="mb-0 text-info expense-card-title">{{ title }}</h3>
      <div class="expense-card-line" style="border-color: #17a2b8"></div>
    </div>

    <div class="table-responsive">
      <table class="table table-sm mb-0">
        <thead>
          <tr>
            <th>Data</th>
            <th>Tipo</th>
            <th>Descrição</th>
            <th v-if="showBillCenter">Centro de Custo</th>
            <th style="min-width: 170px">Valor</th>
            <th>Comprovante</th>
            <th>Situação</th>
            <th v-if="reviewing" style="min-width: 230px">Ação</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id" :class="rowClass(item)">
            <td>{{ item.date_reimbursement_f }}</td>
            <td>{{ item.reimbursement_type ? item.reimbursement_type.name : "-" }}</td>
            <td>{{ item.expense_description }}</td>
            <td v-if="showBillCenter">{{ billCenterNames(item) }}</td>
            <td>
              <template v-if="editingAmount[item.id] !== undefined">
                <div class="d-flex align-items-center">
                  <input
                    v-model="editingAmount[item.id]"
                    type="number"
                    step="0.01"
                    min="0"
                    :class="['form-control form-control-sm', inputClass]"
                    style="width: 110px"
                    @keyup.enter="saveAmount(item)"
                  />
                  <button class="btn btn-sm btn-success ml-50" title="Salvar" @click="saveAmount(item)">
                    <i class="fas fa-check"></i>
                  </button>
                  <button class="btn btn-sm btn-secondary ml-50" title="Cancelar" @click="$delete(editingAmount, item.id)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </template>
              <template v-else>
                {{ formatCurrency(item.amount) }}
                <a v-if="reviewing" href="#" class="ml-50" title="Editar valor" @click.prevent="startEditAmount(item)">
                  <i class="fas fa-pen"></i>
                </a>
                <div v-if="item.original_amount !== null && item.original_amount !== undefined" class="text-muted" style="font-size: 11px">
                  Valor informado: {{ formatCurrency(item.original_amount) }}
                </div>
              </template>
            </td>
            <td>
              <a v-if="item.file" :href="item.file_url" target="_blank" :title="item.file">
                <i class="fas fa-download"></i>
              </a>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span :class="['badge', itemStatus(item).badge]">{{ itemStatus(item).text }}</span>
              <div v-if="Number(item.approve) === 2 && item.observation" class="text-danger" style="font-size: 11px">
                {{ item.observation }}
              </div>
              <div v-if="contest(item)" class="mt-50">
                <span :class="['badge', contest(item).badge]">{{ contest(item).text }}</span>
                <a href="#" class="ml-50" style="font-size: 12px" @click.prevent="openContestView(item)">
                  <i class="fas fa-eye"></i> Ver contestação
                </a>
              </div>
              <button
                v-else-if="canContest && Number(item.approve) === 2"
                class="btn btn-sm btn-outline-warning mt-50"
                @click="openContestForm(item)"
              >
                <i class="fas fa-gavel"></i> Contestar
              </button>
            </td>
            <td v-if="reviewing">
              <template v-if="rejectingItem === item.id">
                <input
                  v-model="rejectItemNote"
                  type="text"
                  placeholder="Motivo da rejeição"
                  :class="['form-control form-control-sm mb-50', inputClass]"
                  @keyup.enter="decide(item, 2)"
                />
                <button class="btn btn-sm btn-danger" @click="decide(item, 2)">Rejeitar</button>
                <button class="btn btn-sm btn-secondary ml-50" @click="rejectingItem = null">Cancelar</button>
              </template>
              <template v-else>
                <button
                  class="btn btn-sm"
                  :class="Number(item.approve) === 1 ? 'btn-success' : 'btn-outline-success'"
                  title="Aprovar"
                  @click="decide(item, 1)"
                >
                  <i class="fas fa-check"></i> Aprovar
                </button>
                <button
                  class="btn btn-sm ml-50"
                  :class="Number(item.approve) === 2 ? 'btn-danger' : 'btn-outline-danger'"
                  title="Rejeitar"
                  @click="startReject(item)"
                >
                  <i class="fas fa-ban"></i> Rejeitar
                </button>
                <a
                  v-if="Number(item.approve) !== 0"
                  href="#"
                  class="ml-50"
                  title="Voltar para pendente"
                  @click.prevent="decide(item, 0)"
                >
                  <i class="fas fa-undo"></i>
                </a>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Colaborador justifica a contestação -->
    <b-modal
      ref="contestForm"
      title="Contestar rejeição da despesa"
      ok-title="Enviar contestação"
      cancel-title="Cancelar"
      ok-variant="warning"
      @ok.prevent="submitContest"
    >
      <template v-if="contestItem">
        <p class="mb-50"><strong>Despesa:</strong> {{ contestItem.expense_description }} ({{ formatCurrency(contestItem.amount) }})</p>
        <p class="mb-1 text-danger" v-if="contestItem.observation"><strong>Motivo da rejeição:</strong> {{ contestItem.observation }}</p>
        <label><span class="text-danger">*</span> Justificativa</label>
        <textarea v-model="contestJustification" class="form-control" rows="4" maxlength="2000"></textarea>
        <small class="text-muted">A solicitação voltará para análise do gestor.</small>
      </template>
    </b-modal>

    <!-- Detalhe da contestação -->
    <b-modal ref="contestView" title="Contestação do colaborador" ok-only ok-title="Fechar">
      <template v-if="contestItem">
        <p class="mb-50"><strong>Despesa:</strong> {{ contestItem.expense_description }} ({{ formatCurrency(contestItem.amount) }})</p>
        <p class="mb-50" v-if="contestItem.contested_at"><strong>Contestada em:</strong> {{ formatDateTime(contestItem.contested_at) }}</p>
        <p class="mb-50" v-if="contest(contestItem)">
          <strong>Situação:</strong> <span :class="['badge', contest(contestItem).badge]">{{ contest(contestItem).text }}</span>
        </p>
        <p class="mb-50"><strong>Justificativa:</strong></p>
        <div style="white-space: pre-line">{{ contestItem.contest_justification }}</div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { contestStatus } from "../../utils/expenseStatus";

const ITEM_STATUS = {
  0: { text: "Pendente", badge: "badge-secondary" },
  1: { text: "Aprovada", badge: "badge-success" },
  2: { text: "Rejeitada", badge: "badge-danger" },
};

/**
 * Tabela de despesas com análise item a item (aprovar, rejeitar com motivo, editar valor).
 * Emite "decide", "save-amount" e "contest" com um callback done() que o pai chama quando a API confirmar.
 */
export default {
  name: "ExpenseReviewTable",
  props: {
    items: { type: Array, default: () => [] },
    reviewing: { type: Boolean, default: false },
    // Colaborador pode contestar as despesas rejeitadas
    canContest: { type: Boolean, default: false },
    showBillCenter: { type: Boolean, default: false },
    title: { type: String, default: "Despesas" },
  },
  data() {
    return {
      rejectingItem: null,
      rejectItemNote: "",
      editingAmount: {},
      contestItem: null,
      contestJustification: "",
    };
  },
  computed: {
    dark() {
      return this.$store.state.appConfig.layout.skin === "dark";
    },
    inputClass() {
      return this.dark ? "expense-input-dark" : "expense-input-light";
    },
  },
  methods: {
    startReject(item) {
      this.rejectingItem = item.id;
      this.rejectItemNote = Number(item.approve) === 2 ? item.observation || "" : "";
    },
    decide(item, approve) {
      if (approve === 2 && !this.rejectItemNote.trim()) {
        this.$message(null, "Informe o motivo da rejeição da despesa", "error");
        return;
      }
      this.$emit("decide", {
        item,
        approve,
        observation: approve === 2 ? this.rejectItemNote.trim() : null,
        done: () => {
          this.rejectingItem = null;
          this.rejectItemNote = "";
        },
      });
    },
    startEditAmount(item) {
      this.$set(this.editingAmount, item.id, Number(item.amount).toFixed(2));
    },
    saveAmount(item) {
      const raw = this.editingAmount[item.id];
      const amount = Number(raw);
      if (raw === "" || !isFinite(amount) || amount < 0) {
        this.$message(null, "Informe um valor válido", "error");
        return;
      }
      this.$emit("save-amount", {
        item,
        amount,
        done: () => this.$delete(this.editingAmount, item.id),
      });
    },
    contest(item) {
      return contestStatus(item.contest_status);
    },
    openContestForm(item) {
      this.contestItem = item;
      this.contestJustification = "";
      this.$refs.contestForm.show();
    },
    openContestView(item) {
      this.contestItem = item;
      this.$refs.contestView.show();
    },
    submitContest() {
      const justification = this.contestJustification.trim();
      if (!justification) {
        this.$message(null, "Informe a justificativa da contestação", "error");
        return;
      }
      this.$emit("contest", {
        item: this.contestItem,
        justification,
        done: () => {
          this.$refs.contestForm.hide();
          this.contestJustification = "";
        },
      });
    },
    formatDateTime(value) {
      const d = new Date(value);
      return isNaN(d) ? value : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    },
    billCenterNames(item) {
      const names = (item.bill_centers || []).map((c) => c.number_name || c.name).filter(Boolean);
      return names.length ? names.join(", ") : "Sem centro de custo";
    },
    itemStatus(item) {
      return ITEM_STATUS[Number(item.approve)] || ITEM_STATUS[0];
    },
    rowClass(item) {
      if (Number(item.contest_status) === 1) return "row-contested";
      const a = Number(item.approve);
      if (a === 1) return "row-approved";
      if (a === 2) return "row-rejected";
      return "";
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);
    },
  },
};
</script>

<style scoped src="./expense-card.css"></style>
<style scoped>
.row-approved {
  background-color: rgba(40, 199, 111, 0.12);
}

.row-rejected {
  background-color: rgba(234, 84, 85, 0.12);
}

.row-contested {
  background-color: rgba(255, 159, 67, 0.15);
}
</style>
